import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { HttpService } from './http.service';
import {
  CreateLinkTemplatePayload,
  LinkTemplate,
  LinkTemplateDetail,
  LinkTemplatesExportPayload,
  LinkTemplatesListMeta,
  LinkTemplatesListParams,
  UpdateLinkTemplatePayload,
} from '../../store/entities/models/link-template.model';
import { transformCamelToSnake, transformSnakeToCamel } from '../../store/utils/transformers';

interface ApiResponse<T> {
  data: T;
  meta?: LinkTemplatesListMeta;
  message?: string;
  success?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LinkTemplatesService {
  private http = inject(HttpService);

  getTemplates(params: LinkTemplatesListParams = {}): Observable<{ data: LinkTemplate[]; meta: LinkTemplatesListMeta }> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? 1,
      per_page: params.perPage ?? 25,
      search: params.search ?? '',
      sort: params.sort ?? 'created_at',
      sort_direction: params.sortDirection ?? 'desc',
    };

    if (params.filters?.length) {
      queryParams.filters = params.filters;
      queryParams.filter_link = params.filterLink ?? 'and';
    }

    return this.http
      .get<Record<string, unknown>>('/templates', queryParams)
      .pipe(map((response) => transformSnakeToCamel(response) as { data: LinkTemplate[]; meta: LinkTemplatesListMeta }));
  }

  getTemplateById(uuid: string): Observable<LinkTemplateDetail> {
    return this.http
      .get<Record<string, unknown>>(`/templates/${uuid}`)
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as ApiResponse<LinkTemplateDetail>;

          return (transformed.data ?? transformed) as LinkTemplateDetail;
        })
      );
  }

  createTemplate(payload: CreateLinkTemplatePayload): Observable<LinkTemplateDetail> {
    return this.http
      .post<Record<string, unknown>>('/templates', this.toApiPayload(payload))
      .pipe(map((response) => (transformSnakeToCamel(response) as ApiResponse<LinkTemplateDetail>).data));
  }

  updateTemplate(uuid: string, payload: UpdateLinkTemplatePayload): Observable<LinkTemplateDetail> {
    return this.http
      .put<Record<string, unknown>>(`/templates/${uuid}`, this.toApiPayload(payload))
      .pipe(map((response) => (transformSnakeToCamel(response) as ApiResponse<LinkTemplateDetail>).data));
  }

  deleteTemplate(uuid: string): Observable<void> {
    return this.http.delete<void>(`/templates/${uuid}`);
  }

  exportTemplates(payload: LinkTemplatesExportPayload): Observable<{ message?: string }> {
    return this.http
      .post<Record<string, unknown>>('/templates/export', transformCamelToSnake(payload))
      .pipe(map((response) => transformSnakeToCamel(response) as { message?: string }));
  }

  setDefaultTemplate(accountUuid: string, templateUuid: string): Observable<unknown> {
    return this.http.patch(
      `/tenants/${accountUuid}`,
      transformCamelToSnake({ defaultLinkTemplateUuid: templateUuid })
    );
  }

  private toApiPayload(payload: CreateLinkTemplatePayload | UpdateLinkTemplatePayload): Record<string, unknown> {
    const request = {
      ...payload,
      groups: payload.groups.map((uuid) => ({ uuid })),
      tags: payload.tags.map((id) => ({ id })),
      availableQrTemplates: payload.availableQrTemplates.map((uuid) => ({ uuid })),
      attributes: payload.attributes.filter((attribute) => attribute.isAssigned),
    };

    if (!request.retentionType || !request.retentionDays || request.retentionDays <= 0) {
      delete request.retentionType;
      delete request.retentionDays;
    }

    return transformCamelToSnake(request);
  }
}
